import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import ToastNotification from "components/common/ToastNotification";
import UnsavedChangesModal from "components/common/UnsavedChangesModal";
import { transformErrors } from "helpers/utils";
import {
  Col,
  Form,
  Row,
  Card,
  Table,
  InputGroup,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFieldArray,
} from "react-hook-form";
import { Navigate, useNavigate, useBlocker } from "react-router-dom";
import {
  PromocaoRequest,
  promocaoBreadcrumbItems,
  ProdutoPromo,
} from "data/promocoes";
import DatePicker from "components/base/DatePicker";
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faSave,
  faTimes,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import LojasAssociadasPromocao from "../promocoes/LojasAssociadasPromocao";
import SelecaoItensOffcanvas, {
  ItemPromocao,
} from "../promocoes/selecionarItensOffCanvas";
import { usePermissionModal } from "contexts/PermissionModalContext";

// Define API response type
interface ApiResponse<T> {
  data: T;
  isSuccess: boolean;
  message: string;
}

const NovaPromocao = () => {
  const navigate = useNavigate();
  const [showModalItens, setShowModalItens] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null
  );
  const isDirtyRef = useRef(false);

  // Add a state to track if the form has been properly initialized
  const [formInitialized, setFormInitialized] = useState(false);

  // Use permission modal hook
  const { showPermissionModal, getMutationErrorHandler } = usePermissionModal();

  const form = useForm<{
    codigo?: number;
    descricao: string;
    inativo: boolean;
    tipoDesconto: number;
    dataInicio?: Date;
    dataFim?: Date;
    ilimitada: boolean;
    acumulativa: boolean;
    pagueLeve: { pague: number; leve: number };
    descontoMultiplo: { quantidade: number; desconto: number }[];
    aplicarPara: number;
    produtoPromocoes: ItemPromocao[];
    empresasAssociadas: number[];
  }>({
    defaultValues: {
      codigo: undefined,
      descricao: "",
      inativo: false,
      tipoDesconto: 2, // Default to DESC_MULTIPLO (2)
      dataInicio: undefined,
      dataFim: undefined,
      ilimitada: false,
      acumulativa: false,
      pagueLeve: { pague: 1, leve: 2 },
      descontoMultiplo: [{ quantidade: 2, desconto: 10 }],
      aplicarPara: 1, // Default to PRODUTO (1)
      produtoPromocoes: [],
      empresasAssociadas: [],
    },
  });

  // Mark form as initialized after initial render
  useEffect(() => {
    // Inicializar o formulário e garantir que ele comece como "limpo"
    form.reset(form.getValues(), {
      keepDirty: false,
      keepTouched: false,
      keepIsValid: true,
      keepIsSubmitted: false,
      keepSubmitCount: false,
    });
    setFormDirty(false);
    isDirtyRef.current = false;
    setFormInitialized(true);
  }, []);

  // Mark form as dirty when values change - improved version
  useEffect(() => {
    if (!formInitialized) return;

    // Watch all form changes
    const subscription = form.watch((value, { name, type }) => {
      if (!formDirty) {
        setFormDirty(true);
      }
      isDirtyRef.current = true;
    });

    // Also listen to form's internal isDirty state
    const formStateSubscription = form.formState.dirtyFields;

    return () => {
      subscription.unsubscribe();
    };
  }, [form, formDirty, formInitialized]);

  // Reset formDirty when saved successfully

  // Configurar o bloqueador de navegação - improved version to match editarPromocao
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Não bloquear se:
    // 1. O formulário não está sujo
    // 2. O formulário está sendo enviado
    // 3. Estamos no processo de salvamento
    // 4. A navegação está na mesma página
    if (!isDirtyRef.current) return false;
    if (form.formState.isSubmitting) return false;
    if (isSaving) return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;

    // Se chegarmos aqui, precisamos mostrar o modal de confirmação
    setPendingNavigation(nextLocation.pathname);
    setShowNavigationModal(true);

    return true;
  });

  // Field array for discount tiers
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "descontoMultiplo",
  });

  // Add useFieldArray for produtoPromocoes
  const { fields: produtoFields, remove: removeProduto } = useFieldArray({
    control: form.control,
    name: "produtoPromocoes",
  });

  // Get current values for conditional UI rendering
  const tipoDesconto = form.watch("tipoDesconto");
  const aplicarPara = form.watch("aplicarPara");
  const produtoPromocoes = form.watch("produtoPromocoes") || [];
  const empresasAssociadas = form.watch("empresasAssociadas") || [];

  // Helper para identificar o tipo do item baseado nos IDs
  const getItemType = (
    item: ProdutoPromo
  ): "produto" | "departamento" | "categoria" => {
    if ((item.produtoId ?? 0) > 0) return "produto";
    if ((item.departamentoId ?? 0) > 0) return "departamento";
    if ((item.categoriaId ?? 0) > 0) return "categoria";
    return "produto"; // Fallback
  };

  // Helper para filtrar itens por tipo
  const getItemsByType = (type: "produto" | "departamento" | "categoria") => {
    return produtoPromocoes.filter((item) => getItemType(item) === type);
  };

  // Helper para obter itens filtrados com base no aplicarPara selecionado
  const getFilteredItems = () => {
    const itemType =
      aplicarPara === 1
        ? "produto"
        : aplicarPara === 2
        ? "departamento"
        : "categoria";
    return produtoPromocoes.filter((item) => getItemType(item) === itemType);
  };

  // Handler to remove an item from the promotion - updated to use useFieldArray
  const handleRemoverItem = (index: number) => {
    removeProduto(index);
  };

  const handleToggleLoja = (lojaId: number) => {
    const updatedLojas = empresasAssociadas.includes(lojaId)
      ? empresasAssociadas.filter((id) => id !== lojaId)
      : [...empresasAssociadas, lojaId];

    // Updated with shouldDirty: true to match editarPromocao
    form.setValue("empresasAssociadas", updatedLojas, { shouldDirty: true });
  };

  // Mutation for saving the promotion
  const mutation = useMutation<ApiResponse<any>, any, PromocaoRequest>({
    mutationFn: async (data) => {
      setIsSaving(true);
      try {
        // Create payload with correct structure
        const promocaoData: PromocaoRequest = {
          ...data,
          ilimitada: data.ilimitada,
          dataInicio: data.dataInicio,
          dataFim: data.dataFim,
          pagueLeve: data.pagueLeve,
          descontoMultiplo:
            data.tipoDesconto === 2 ? data.descontoMultiplo : [],
          aplicarPara: data.aplicarPara,
          // Limpa os dados para enviar apenas o que é necessário
          produtoPromocoes: data.produtoPromocoes.map((item) => {
            return {
              produtoId: (item.produtoId ?? 0) > 0 ? item.produtoId : undefined,
              departamentoId:
                (item.departamentoId ?? 0) > 0
                  ? item.departamentoId
                  : undefined,
              categoriaId:
                (item.categoriaId ?? 0) > 0 ? item.categoriaId : undefined,
            } as ProdutoPromo;
          }),
          empresasAssociadas: data.empresasAssociadas || [],
        };

        const response = await axios.post("/api/promocao", promocaoData);
        return response.data;
      } finally {
        setIsSaving(false);
      }
    },
    onError: getMutationErrorHandler(
      "Você não possui permissão para cadastrar promoções.",
      (error) => {
        // Handle non-permission errors
        if (error?.response?.data?.errors) {
          const formattedErrors = transformErrors(error.response.data.errors);
          for (const [field, error] of Object.entries(formattedErrors)) {
            form.setError(field as any, error as any);
          }
          setErrorMessage("Verifique os erros no formulário.");
          setShowErrorToast(true);
        } else if (error?.response?.data?.message) {
          // Handle API error message
          setErrorMessage(error.response.data.message);
          setShowErrorToast(true);
        } else {
          setErrorMessage("Ocorreu um erro ao salvar a promoção.");
          setShowErrorToast(true);
        }
      }
    ),
    onSuccess: (response) => {
      if (response.isSuccess) {
        setFormDirty(false);
        isDirtyRef.current = false;
        const codigo = response.data?.codigo || "";
        navigate(`/cadastros/promocoes?successCode=${codigo}&new=true`);
      }
    },
  });

  useEffect(() => {
    if (mutation.isSuccess && mutation.data?.isSuccess) {
      setFormDirty(false);
      isDirtyRef.current = false;
    }
  }, [mutation.isSuccess, mutation.data?.isSuccess]);

  // Add useEffect to handle API error responses properly
  useEffect(() => {
    // When mutation is successful but API returns isSuccess: false
    if (mutation.isSuccess && !mutation.data?.isSuccess) {
      setErrorMessage(
        mutation.data.message || "Ocorreu um erro ao criar a promoção."
      );
      setShowErrorToast(true);
    }
  }, [mutation.isSuccess, mutation.data]);

  // Form submission handler with validation
  const onSubmit: SubmitHandler<any> = async (data) => {
    // Determina qual tipo de item deve ser validado com base em aplicarPara
    let itemType: "produto" | "departamento" | "categoria";
    let errorText: string;

    switch (data.aplicarPara) {
      case 1:
        itemType = "produto";
        errorText = "produto";
        break;
      case 2:
        itemType = "departamento";
        errorText = "departamento";
        break;
      case 3:
        itemType = "categoria";
        errorText = "categoria";
        break;
      default:
        itemType = "produto";
        errorText = "item";
    }

    // Valida se há itens do tipo correto selecionados
    if (getItemsByType(itemType).length === 0) {
      setErrorMessage(`Selecione ao menos um item para a promoção.`);
      setShowErrorToast(true);
      return;
    }

    // Valida datas quando não é ilimitada
    if (!data.ilimitada) {
      if (!data.dataInicio || !data.dataFim) {
        setErrorMessage("Período de vigência deve ter data inicial e final.");
        setShowErrorToast(true);
        return;
      }

      // Valida período
      if (new Date(data.dataInicio) > new Date(data.dataFim)) {
        setErrorMessage("A data final deve ser posterior à data inicial.");
        setShowErrorToast(true);
        return;
      }
    }

    // Valida campos de desconto múltiplo
    if (
      data.tipoDesconto === 2 &&
      (!data.descontoMultiplo || data.descontoMultiplo.length === 0)
    ) {
      setErrorMessage(
        "Promoção do tipo Desconto Múltiplo deve ter pelo menos uma faixa configurada."
      );
      setShowErrorToast(true);
      return;
    }

    // Resetar o estado de sujeira antes de mutar
    setIsSaving(true);
    mutation.mutate(data);
  };

  // Helper function to add a new discount tier
  const handleAdicionarDescontoMultiplo = () => {
    const ultimaQtd =
      fields.length > 0 ? fields[fields.length - 1].quantidade + 1 : 2;
    append({ quantidade: ultimaQtd, desconto: 10 });
  };

  // Função para lidar com a confirmação de navegação
  const handleNavigationConfirm = () => {
    setShowNavigationModal(false);

    if (pendingNavigation) {
      // Resetar o estado de sujeira antes de navegar
      isDirtyRef.current = false;
      setFormDirty(false);

      // Resetar o formulário para os valores padrão
      form.reset(
        {
          codigo: undefined,
          descricao: "",
          inativo: false,
          tipoDesconto: 2,
          dataInicio: undefined,
          dataFim: undefined,
          ilimitada: false,
          acumulativa: false,
          pagueLeve: { pague: 1, leve: 2 },
          descontoMultiplo: [{ quantidade: 2, desconto: 10 }],
          aplicarPara: 1,
          produtoPromocoes: [],
          empresasAssociadas: [],
        },
        {
          keepValues: false,
          keepDirty: false,
          keepIsSubmitted: false,
          keepTouched: false,
          keepIsValid: false,
          keepSubmitCount: false,
        }
      );

      // Desbloquear e navegar
      blocker.proceed?.();
      navigate(pendingNavigation);
    }
  };

  // Função para cancelar a navegação
  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    setPendingNavigation(null);
    blocker.reset?.();
  };

  // Simplified navigation check - just navigate immediately on success
  if (mutation.isSuccess && mutation.data?.isSuccess) {
    return (
      <Navigate
        to={`/cadastros/promocoes?successCode=${
          mutation.data.data?.codigo || ""
        }&new=true`}
      />
    );
  }

  // Return component JSX
  return (
    <div>
      <FormProvider {...form}>
        <UnsavedChangesModal
          show={showNavigationModal}
          onConfirm={handleNavigationConfirm}
          onCancel={handleNavigationCancel}
        />

        <PageBreadcrumb items={promocaoBreadcrumbItems()} />
        <Form
          noValidate
          className="mb-9"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-2">Cadastrar uma Promoção</h2>
              <h5 className="text-body-tertiary fw-semibold">
                Preencha as informações da promoção
              </h5>
            </div>
            <Button
              variant="primary"
              type="submit"
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Salvar Promoção
            </Button>
          </div>

          {/* Error Toast */}
          <ToastNotification
            show={showErrorToast}
            type="error"
            message={errorMessage}
            onClose={() => setShowErrorToast(false)}
          />

          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">Informações Básicas</h5>
              <Row className="mb-3">
                <Col xs={12}>
                  <Form.Group controlId="descricao">
                    <Form.Label>
                      Descrição <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      {...form.register("descricao", {
                        required: "A descrição é obrigatória",
                      })}
                      placeholder="Ex: Promoção de Neosoro"
                      isInvalid={!!form.formState.errors.descricao}
                    />
                    <Form.Control.Feedback type="invalid">
                      {form.formState.errors.descricao?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <Form.Group controlId="tipoDesconto">
                    <Form.Label>Tipo de Desconto</Form.Label>
                    <Form.Select
                      {...form.register("tipoDesconto", {
                        valueAsNumber: true,
                      })}
                    >
                      <option value={1}>Pague e Leve</option>
                      <option value={2}>Desconto Múltiplo</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group controlId="aplicarPara">
                    <Form.Label>Aplicar Para</Form.Label>
                    <Form.Select
                      {...form.register("aplicarPara", { valueAsNumber: true })}
                    >
                      <option value={1}>Produtos</option>
                      <option value={2}>Departamentos</option>
                      <option value={3}>Categorias</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col>
                  <Form.Check
                    type="switch"
                    id="acumulativa"
                    label="Permitir combinar com outras promoções"
                    {...form.register("acumulativa")}
                  />
                </Col>
                <Col>
                  <Form.Check
                    type="switch"
                    id="inativo"
                    label="Inativo"
                    {...form.register("inativo")}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">Período de Vigência</h5>
              <div className="mb-3">
                <Form.Group>
                  <div className="mb-3">
                    <Form.Check
                      inline
                      type="radio"
                      id="vigenciaPeriodo"
                      label="Por Período"
                      checked={form.watch("ilimitada") === false}
                      onChange={() => {
                        form.setValue("ilimitada", false, {
                          shouldDirty: true,
                        });
                      }}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="vigenciaIlimitada"
                      label="Ilimitada"
                      checked={form.watch("ilimitada") === true}
                      onChange={() => {
                        form.setValue("ilimitada", true, { shouldDirty: true });
                      }}
                    />
                  </div>
                </Form.Group>
              </div>

              {form.watch("ilimitada") === false && (
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Data de Início <span className="text-danger">*</span>
                      </Form.Label>
                      <div
                        className={
                          form.formState.errors.dataInicio ? "is-invalid" : ""
                        }
                      >
                        <DatePicker
                          name="dataInicio"
                          onChange={(date) =>
                            form.setValue(
                              "dataInicio",
                              Array.isArray(date) ? date[0] : date,
                              { shouldDirty: true }
                            )
                          }
                          value={form.watch("dataInicio")}
                        />
                      </div>
                      <Form.Control.Feedback
                        type="invalid"
                        className={
                          !!form.formState.errors.dataInicio ? "d-block" : ""
                        }
                      >
                        {form.formState.errors.dataInicio?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Data de Fim <span className="text-danger">*</span>
                      </Form.Label>
                      <div
                        className={
                          form.formState.errors.dataFim ? "is-invalid" : ""
                        }
                      >
                        <DatePicker
                          name="dataFim"
                          onChange={(date) =>
                            form.setValue(
                              "dataFim",
                              Array.isArray(date) ? date[0] : date,
                              { shouldDirty: true }
                            )
                          }
                          value={form.watch("dataFim")}
                        />
                      </div>
                      <Form.Control.Feedback
                        type="invalid"
                        className={
                          !!form.formState.errors.dataFim ? "d-block" : ""
                        }
                      >
                        {form.formState.errors.dataFim?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>

          {tipoDesconto === 1 && ( // PAGUE_LEVE = 1
            <Card className="mb-4">
              <Card.Body>
                <h5 className="mb-3">Configuração de Pague e Leve</h5>
                <Row>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Pague (quantidade){" "}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        {...form.register("pagueLeve.pague", {
                          required: "Quantidade é obrigatória",
                          min: {
                            value: 1,
                            message: "Quantidade deve ser pelo menos 1",
                          },
                          valueAsNumber: true,
                        })}
                        isInvalid={!!form.formState.errors.pagueLeve?.pague}
                      />
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.pagueLeve?.pague?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group>
                      <Form.Label>
                        Leve (quantidade) <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        {...form.register("pagueLeve.leve", {
                          required: "Quantidade é obrigatória",
                          min: {
                            value: 1,
                            message: "Quantidade deve ser pelo menos 1",
                          },
                          valueAsNumber: true,
                        })}
                        isInvalid={!!form.formState.errors.pagueLeve?.leve}
                      />
                      <Form.Control.Feedback type="invalid">
                        {form.formState.errors.pagueLeve?.leve?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {tipoDesconto === 2 && ( // DESC_MULTIPLO = 2
            <Card className="mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Configuração de Desconto Múltiplo</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAdicionarDescontoMultiplo}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Adicionar Faixa
                  </Button>
                </div>

                <Table responsive>
                  <thead>
                    <tr>
                      <th>Quantidade</th>
                      <th>Desconto (%)</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((field, index) => (
                      <tr key={field.id}>
                        <td>
                          <Form.Control
                            type="number"
                            min="1"
                            step="1"
                            {...form.register(
                              `descontoMultiplo.${index}.quantidade`,
                              {
                                required: "Obrigatório",
                                min: {
                                  value: 1,
                                  message: "Min: 1",
                                },
                                valueAsNumber: true,
                              }
                            )}
                            style={{ width: "100px" }}
                            isInvalid={
                              !!form.formState.errors.descontoMultiplo?.[index]
                                ?.quantidade
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {
                              form.formState.errors.descontoMultiplo?.[index]
                                ?.quantidade?.message
                            }
                          </Form.Control.Feedback>
                        </td>
                        <td>
                          <InputGroup>
                            <Form.Control
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              {...form.register(
                                `descontoMultiplo.${index}.desconto`,
                                {
                                  required: "Obrigatório",
                                  min: {
                                    value: 0,
                                    message: "Min: 0",
                                  },
                                  max: {
                                    value: 100,
                                    message: "Max: 100",
                                  },
                                  valueAsNumber: true,
                                }
                              )}
                              style={{ width: "100px" }}
                              isInvalid={
                                !!form.formState.errors.descontoMultiplo?.[
                                  index
                                ]?.desconto
                              }
                            />
                            <InputGroup.Text>%</InputGroup.Text>
                            <Form.Control.Feedback type="invalid">
                              {
                                form.formState.errors.descontoMultiplo?.[index]
                                  ?.desconto?.message
                              }
                            </Form.Control.Feedback>
                          </InputGroup>
                        </td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => remove(index)}
                            disabled={fields.length === 1}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          <Card className="mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Itens da Promoção</h5>
                <Button
                  variant="primary"
                  onClick={() => setShowModalItens(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Adicionar Itens
                </Button>
              </div>

              {produtoPromocoes.length === 0 ? (
                <div>
                  <p className="text-muted">
                    Nenhum item adicionado à promoção.
                  </p>
                </div>
              ) : (
                <>
                  <p className="mb-2">
                    {aplicarPara === 1
                      ? "Produtos "
                      : aplicarPara === 2
                      ? "Departamentos "
                      : "Categorias "}
                    selecionados ({getFilteredItems().length}):
                  </p>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {getFilteredItems().map((item, index) => {
                      const tipo = getItemType(item);
                      const textColor =
                        tipo === "produto"
                          ? "primary"
                          : tipo === "departamento"
                          ? "success"
                          : "info";

                      return (
                        <Badge
                          key={index}
                          text={textColor}
                          className="d-flex align-items-center py-1 px-2 border bg-body"
                          style={{ borderRadius: "16px" }}
                        >
                          <span className="me-1 text-primary">
                            {item.descricao}
                          </span>
                          <Button
                            variant="link"
                            className="p-0 ms-1 text-primary border-0"
                            onClick={() => handleRemoverItem(index)}
                            aria-label="Remover item"
                            style={{ fontSize: "12px" }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </Badge>
                      );
                    })}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>

          <LojasAssociadasPromocao
            selectedLojas={empresasAssociadas}
            onToggleLoja={handleToggleLoja}
          />

          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/cadastros/promocoes")}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={mutation.isPending}
              disabled={mutation.isPending}
            >
              <FontAwesomeIcon icon={faSave} className="me-2" />
              Salvar Promoção
            </Button>
          </div>
        </Form>
      </FormProvider>
      <SelecaoItensOffcanvas
        show={showModalItens}
        onHide={() => setShowModalItens(false)}
        onConfirm={(selectedItems) => {
          form.setValue("produtoPromocoes", selectedItems, {
            shouldDirty: true,
          });
          setFormDirty(true);
        }}
        selectedItems={produtoPromocoes}
        aplicarPara={
          aplicarPara === 1
            ? "PRODUTO"
            : aplicarPara === 2
            ? "DEPARTAMENTO"
            : "CATEGORIA"
        }
      />
    </div>
  );
};

export default NovaPromocao;
